import React from 'react';
import { useTranslation } from 'react-i18next';
import { AssetDetailsResponse } from '../../../api/asset/assetApiTypes';
import AssetDetailSection from './AssetDetailSection';
import DetailField from './DetailField';
import InputField from '../../../components/ui/InputField';

interface LocationSectionProps {
    data: AssetDetailsResponse;
    mode: 'view' | 'edit' | 'create';
    /** Editable floor value (edit mode). */
    floorValue?: string;
    onFloorChange?: (floor: string) => void;
}

/**
 * Extract the floor designation from an asset name.
 * Pattern: "[Street Address], [Floor Designation] | [District]"
 * Returns the text between the first comma and the pipe, trimmed.
 * Returns null if the pattern doesn't match.
 */
export function extractFloorFromName(name: string | undefined): string | null {
    if (!name) return null;
    const commaIdx = name.indexOf(',');
    const pipeIdx = name.indexOf('|');
    if (commaIdx === -1 || pipeIdx === -1 || commaIdx >= pipeIdx) return null;
    const floor = name.substring(commaIdx + 1, pipeIdx).trim();
    return floor.length > 0 ? floor : null;
}

const LocationSection: React.FC<LocationSectionProps> = ({ data, mode, floorValue, onFloorChange }) => {
    const { t } = useTranslation();
    const location = data.Group?.location;
    const address = location?.address;

    // View mode: prefer the dedicated floor column, fall back to the legacy
    // floor-in-name convention for assets created before it was first-class.
    const viewFloor = data.floor ?? extractFloorFromName(data.name);

    return (
        <AssetDetailSection title={t('asset_detail.sections.location')}>
            <div className={'grid grid-cols-1 gap-4'}>
                <div className={'grid grid-cols-2 gap-4'}>
                    <DetailField
                        label={t('asset_detail.fields.street')}
                        value={address?.street && address?.houseNumber
                            ? `${address.street} ${address.houseNumber}`
                            : address?.street}
                    />
                    {mode === 'edit' ? (
                        <InputField
                            label={t('asset_detail.fields.floor_number')}
                            value={floorValue ?? ''}
                            onChange={(val) => onFloorChange?.(val)}
                            placeholder={t('asset_detail.fields.floor_placeholder')}
                        />
                    ) : (
                        <DetailField
                            label={t('asset_detail.fields.floor_number')}
                            value={viewFloor}
                            emptyLabel={'na'}
                        />
                    )}
                </div>
                <div className={'grid grid-cols-2 gap-4'}>
                    <DetailField
                        label={t('asset_detail.fields.postal_code')}
                        value={address?.postalCode}
                    />
                    <DetailField
                        label={t('asset_detail.fields.city')}
                        value={address?.city}
                    />
                </div>
                <DetailField
                    label={t('asset_detail.fields.district')}
                    value={address?.district}
                />
                {mode === 'edit' && (
                    <p className={'mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'}>
                        {t('asset_detail.validation.address_immutable_note')}
                    </p>
                )}
            </div>
        </AssetDetailSection>
    );
};

export default LocationSection;
